import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { VendorReplyOnExperienceFormData } from "@/mk2/apps/wiki/components/VendorReplyOnExperienceForm";
import {
    articleDeleteVendorReplyOnExperienceTrigger,
    articleLoadMoreReviewCategoryArticlesTrigger,
    articleSaveVendorReplyOnExperienceTrigger,
} from "@/mk2/apps/wiki/containers/Article/Article.actions";
import { ReviewCategoriesStats } from "@/mk2/apps/wiki/containers/Article/Article.reducers";
import { getWikiArticleState } from "@/mk2/apps/wiki/containers/Article/Article.selectors";
import ArticlePage, {
    DispatchProps as ArticleDispatchProps,
    OwnProps,
    RouteProps,
    StateProps,
} from "@/mk2/apps/wiki/containers/Article/ArticlePage";
import ReviewArticle from "@/mk2/apps/wiki/containers/Article/ReviewArticle";
import {
    WIKI_REVIEWS_ARTICLE_SLUG,
    isArticleOfReviewsCategory,
    isReviewHomepageArticle,
} from "@/mk2/apps/wiki/helpers";
import {
    articleWiki,
    conceptsBreadcrumbsWiki,
    entitiesForArticle,
    reviewArticleWiki,
    reviewableSubjectForArticle,
    videoObjectsForArticle,
} from "@/mk2/apps/wiki/structuredData";
import {
    getWikiArticleUrl,
    wikiAddExperienceToUndefinedArticleUrl,
    wikiAddExperienceUrl,
} from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { TrackClick } from "@/mk2/components/TrackClick";
import { hasVal } from "@/mk2/helpers/sanitize";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import { BrandingProps, HalfpageProps } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import {
    ExperienceEntity,
    ExperienceSchema,
    ReviewCategoryEntity,
    ReviewCategorySchema,
    WikiArticleEntity,
    WikiArticleSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import {
    SEO_DESCR_REVIEW_A_score_count_snippet,
    SEO_DESCR_REVIEW_B_score_count,
    SEO_DESCR_REVIEW_NO_RATING_product,
    WIKI_WRITE_EXPERIENCE,
} from "@/mk/autogenerated/translation";

interface DispatchProps extends ArticleDispatchProps {
    onDeleteVendorReply(experienceId: number);
    onLoadMoreReviewCategoryArticles(articleSlug: string, categorySlug: string, after: number);
    onSaveVendorReply(experienceId: number, values: VendorReplyOnExperienceFormData);
}

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ReviewArticlePage extends ArticlePage {
    public render(props: Props): false | JSX.Element {
        return <ReviewArticle {...props} />;
    }

    public getBrandingProps(props: Props): BrandingProps {
        return null;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return !isReviewHomepageArticle(props.categorySlug, props.articleSlug);
    }

    public getDefaultBackPath(props: Props): string {
        return getWikiArticleUrl(WIKI_REVIEWS_ARTICLE_SLUG, WIKI_REVIEWS_ARTICLE_SLUG);
    }

    public getStructuredData(props: Props): object[] {
        const { article, articleConceptPath, baseUrl, reviewCategoryArticlesCount } = props;
        if (article) {
            const isProductReview =
                isArticleOfReviewsCategory(article.category.slug) &&
                (reviewCategoryArticlesCount === null ||
                    reviewCategoryArticlesCount === undefined) &&
                !isReviewHomepageArticle(article.category.slug, article.slug);
            return [
                conceptsBreadcrumbsWiki(baseUrl, articleConceptPath),
                !isProductReview
                    ? articleWiki(baseUrl, article)
                    : reviewArticleWiki(baseUrl, article),
                // Reviewable #subject node only on the Article path — the Product path
                // (reviewArticleWiki) already carries review + aggregateRating itself.
                ...(!isProductReview ? reviewableSubjectForArticle(baseUrl, article) : []),
                ...entitiesForArticle(article),
                ...videoObjectsForArticle(article),
            ];
        }
    }

    public getAddExperienceBtn(props: Props): React.ReactElement {
        const { article, reviewCategoryArticlesCount } = props;
        let addBtn;
        if (article.experiencesAllowed) {
            const addExpUrl = url(wikiAddExperienceUrl, {
                articleSlug: article.slug,
                categorySlug: article.category.slug,
            });
            addBtn = (
                <TrackClick
                    name="wiki_experience_add_clicked"
                    key="addBtn"
                    props={{
                        article_id: article.id,
                        category_slug: article.category.slug,
                        article_slug: article.slug,
                    }}
                >
                    <Btn
                        key="addBtn"
                        type={BtnType.Blue}
                        icon={faPencil}
                        label={WIKI_WRITE_EXPERIENCE}
                        link={addExpUrl}
                    />
                </TrackClick>
            );
        }

        const isReviewCategory = hasVal(reviewCategoryArticlesCount);
        const isReviewHP = isReviewHomepageArticle(article.category.slug, article.slug);
        const isReviewHomepageOrCategory = isReviewHP || isReviewCategory;
        if (isReviewHomepageOrCategory) {
            const addExpUrl = url(wikiAddExperienceToUndefinedArticleUrl, {});
            addBtn = (
                <TrackClick
                    name="wiki_experience_add_clicked"
                    key="addBtn"
                    props={{
                        article_id: article.id,
                        category_slug: article.category.slug,
                        article_slug: article.slug,
                    }}
                >
                    <Btn
                        key="addBtn"
                        type={BtnType.Blue}
                        icon={faPencil}
                        label={WIKI_WRITE_EXPERIENCE}
                        link={addExpUrl}
                    />
                </TrackClick>
            );
        }
        return addBtn;
    }

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const articleSlug = ownProps.routeParams.articleSlug;
            const categorySlug = ownProps.routeParams.categorySlug;
            const articleState = getWikiArticleState(state, categorySlug, articleSlug);

            const reviewCategoryArticles: WikiArticleEntity[] =
                getDenormalizedEntities<WikiArticleEntity>(
                    state,
                    WikiArticleSchema,
                    articleState.reviewCategoryArticlesIds || [],
                );
            const reviewCategoryArticlesCount = articleState.reviewCategoryArticlesCount;
            const reviewCategoryExperiences: ExperienceEntity[] =
                getDenormalizedEntities<ExperienceEntity>(
                    state,
                    ExperienceSchema,
                    articleState.reviewCategoryArticlesExperiencesIds || [],
                );
            const reviewCategories: ReviewCategoryEntity[] =
                getDenormalizedEntities<ReviewCategoryEntity>(
                    state,
                    ReviewCategorySchema,
                    articleState.reviewCategoriesIds || [],
                );
            const reviewCategoriesStats: ReviewCategoriesStats = articleState.reviewCategoriesStats;

            return {
                ...(super.mapStateToProps()(state, ownProps) as StateProps),
                reviewCategoryArticles,
                reviewCategoryArticlesCount,
                reviewCategoryExperiences,
                reviewCategories,
                reviewCategoriesStats,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...super.mapDispatchToProps(),
            onDeleteVendorReply: articleDeleteVendorReplyOnExperienceTrigger,
            onLoadMoreReviewCategoryArticles: articleLoadMoreReviewCategoryArticlesTrigger,
            onSaveVendorReply: articleSaveVendorReplyOnExperienceTrigger,
        };
    }

    public getMetaDescription(props: Props): string {
        const { article } = props;
        if (!article) {
            return null;
        }

        if (article.experiencesCount > 0 && hasVal(article.experiencesScore)) {
            const score = article.experiencesScore.toFixed(1).replace(".", ",");
            const count = article.experiencesCount;
            const snippet = this.getReviewSnippet(article);
            if (snippet) {
                return interpolate(SEO_DESCR_REVIEW_A_score_count_snippet, {
                    count,
                    score,
                    snippet,
                });
            }
            return interpolate(SEO_DESCR_REVIEW_B_score_count, { count, score });
        }

        return interpolate(SEO_DESCR_REVIEW_NO_RATING_product, { product: article.title });
    }

    private getReviewSnippet(article: WikiArticleEntity): string {
        const MIN_LEN = 40;
        const MAX_LEN = 75;
        const MIN_STARS = 4;
        const normalize = (text: string): string => text.trim().replace(/\s+/g, " ");

        const experience = (article.experiences ?? []).find(
            (exp) =>
                exp.approved &&
                !exp.deleted &&
                exp.starsCount >= MIN_STARS &&
                exp.text &&
                normalize(exp.text).length >= MIN_LEN,
        );
        if (!experience) {
            return null;
        }

        const text = normalize(experience.text);
        const chars = Array.from(text);
        if (chars.length <= MAX_LEN) {
            return text;
        }

        const truncated = chars.slice(0, MAX_LEN).join("");
        const lastSpace = truncated.lastIndexOf(" ");
        return lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
    }
}
